<template>
  <component
    :is="elementType"
    ref="inputElement"
    :value="modelValue"
    :class="{ 'auto-resize-input': autoResize }"
    @input="(event: InputEvent) => handleInput(event)"
    @focusin="enableIosContextMenu"
    @focusout="disableIosContextMenu"
  />
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue'

import {
  enableIosContextMenu,
  disableIosContextMenu,
} from '@/services/capacitor.contextMenuPlugin'

const props = withDefaults(
  defineProps<{
    elementType?: 'input' | 'textarea'
    modelValue?: string
    autoResize?: boolean
  }>(),
  {
    elementType: 'input',
    modelValue: '',
    autoResize: false,
  },
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
}>()

const inputElement = ref<HTMLInputElement | null>(null)

/**
 * If the component has `autoResize`, resize it on load
 * to make sure it renders at the correct height.
 */
onMounted(() => {
  if (!inputElement.value) {
    return
  }

  if (props.autoResize && props.elementType === 'textarea') {
    resizeTextArea(inputElement.value)
  }
})

/**
 * Emit the input value. If the component has `autoResize`,
 * update its height.
 */
function handleInput(event: InputEvent): void {
  const target = event.target as HTMLInputElement

  emit('update:modelValue', target.value)

  if (props.autoResize && props.elementType === 'textarea') {
    resizeTextArea(target)
  }
}

/**
 * Update the text area's element's height based on its contents.
 */
function resizeTextArea(element: HTMLInputElement): void {
  // Reset height to 'auto' to ensure the `scrollHeight` is accurate before resizing.
  element.style.height = 'auto'
  element.style.height = `${element.scrollHeight}px`
}
</script>

<style lang="scss" scroped>
.auto-resize-input {
  -ms-overflow-style: none;
  scrollbar-width: none;
  overflow: hidden;

  &::-webkit-scrollbar {
    display: none;
  }
}
</style>
